import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  TreeSelect,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DeleteOutlined,
  EditOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import type { Tables, TablesInsert } from '@/lib/supabase'
import { AnimeMembershipDrawer } from '@/components/AnimeMembershipDrawer'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/anime-types')({
  component: AnimeTypesPage,
})

type Row = Tables<'anime_types'>

// 手机卡片列表分页（桌面表格仍是 50 条/页）
const MOBILE_PAGE_SIZE = 20

interface FormValues {
  name: string
  slug: string
  parent_id: number | null
  is_active: boolean
  sort_order: number | null
}

function AnimeTypesPage() {
  const qc = useQueryClient()
  const { message, modal } = AntdApp.useApp()
  const [editing, setEditing] = useState<Row | 'new' | null>(null)
  const [managing, setManaging] = useState<Row | null>(null)
  const isMobile = useIsMobile()
  const [mobilePage, setMobilePage] = useState(1)

  const listQuery = useQuery({
    queryKey: ['anime_types', 'list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('anime_types')
        .select('*')
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('name', { ascending: true })
      if (error) throw error
      return data
    },
  })

  const idToRow = useMemo(() => {
    const m = new Map<number, Row>()
    for (const r of listQuery.data ?? []) m.set(r.id, r)
    return m
  }, [listQuery.data])

  const createMutation = useMutation({
    mutationFn: async (input: TablesInsert<'anime_types'>) => {
      const { error } = await supabase.from('anime_types').insert(input)
      if (error) throw error
    },
    onSuccess: () => {
      message.success('已新增')
      qc.invalidateQueries({ queryKey: ['anime_types'] })
      setEditing(null)
    },
    onError: (e: Error) => message.error(e.message || '新增失败'),
  })

  const updateMutation = useMutation({
    mutationFn: async (input: { id: number; patch: FormValues }) => {
      const { error } = await supabase
        .from('anime_types')
        .update(input.patch)
        .eq('id', input.id)
      if (error) throw error
    },
    onSuccess: () => {
      message.success('已保存')
      qc.invalidateQueries({ queryKey: ['anime_types'] })
      setEditing(null)
    },
    onError: (e: Error) => message.error(e.message || '保存失败'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase.from('anime_types').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      message.success('已删除')
      qc.invalidateQueries({ queryKey: ['anime_types'] })
    },
    onError: (e: Error) => message.error(e.message || '删除失败'),
  })

  function handleDelete(row: Row) {
    modal.confirm({
      title: `删除「${row.name}」？`,
      content: '此操作不可撤销。如有番剧引用该类型，删除会失败。',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => deleteMutation.mutateAsync(row.id),
    })
  }

  const columns: ColumnsType<Row> = [
    { title: 'ID', dataIndex: 'id', width: 64 },
    {
      title: '名称',
      dataIndex: 'name',
      render: (v: string) => <span style={{ fontWeight: 500 }}>{v}</span>,
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      width: 160,
      render: (v: string) => (
        <Typography.Text code style={{ fontSize: 12 }}>
          {v}
        </Typography.Text>
      ),
    },
    {
      title: '父类型',
      width: 160,
      render: (_: unknown, row) =>
        row.parent_id ? (
          (idToRow.get(row.parent_id)?.name ?? `#${row.parent_id}`)
        ) : (
          <span style={{ color: 'var(--ant-color-text-quaternary)' }}>—</span>
        ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 90,
      render: (v: boolean | null) => (
        <Tag color={v === false ? 'default' : 'success'}>
          {v === false ? '停用' : '启用'}
        </Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      width: 80,
      render: (v: number | null) => v ?? '—',
    },
    {
      title: '操作',
      width: 200,
      fixed: 'right',
      render: (_: unknown, row) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            icon={<UnorderedListOutlined />}
            onClick={() => setManaging(row)}
          >
            番剧
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditing(row)}
          >
            编辑
          </Button>
          <Popconfirm
            title="删除？"
            okText="删除"
            okButtonProps={{ danger: true }}
            cancelText="取消"
            onConfirm={() => handleDelete(row)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Card
        title="番剧类型"
        extra={
          <Space>
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => listQuery.refetch()}
                loading={listQuery.isFetching && !listQuery.isLoading}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing('new')}
            >
              新增
            </Button>
          </Space>
        }
      >
        <Table<Row>
          className="admin-table-wrap"
          rowKey="id"
          size="middle"
          loading={listQuery.isLoading}
          columns={columns}
          dataSource={listQuery.data ?? []}
          pagination={{
            pageSize: 50,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条`,
          }}
          scroll={{ x: 700 }}
        />
        {isMobile ? (
          <AdminMobileList<Row>
            rows={(listQuery.data ?? []).slice(
              (mobilePage - 1) * MOBILE_PAGE_SIZE,
              mobilePage * MOBILE_PAGE_SIZE,
            )}
            rowKey={(row) => row.id}
            loading={listQuery.isLoading}
            page={mobilePage}
            pageSize={MOBILE_PAGE_SIZE}
            total={listQuery.data?.length ?? 0}
            onPageChange={setMobilePage}
            renderItem={(row) => (
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                aria-label={`编辑类型「${row.name}」`}
                onClick={() => setEditing(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setEditing(row)
                  }
                }}
              >
                <div className="admin-mobile-record__head">
                  <div className="admin-mobile-record__title">{row.name}</div>
                  <Tag
                    color={row.is_active === false ? 'default' : 'success'}
                    style={{ marginInlineEnd: 0 }}
                  >
                    {row.is_active === false ? '停用' : '启用'}
                  </Tag>
                </div>
                <div className="admin-mobile-record__meta">
                  <Typography.Text code style={{ fontSize: 12 }}>
                    {row.slug}
                  </Typography.Text>
                  {row.parent_id ? (
                    <span>
                      父类型{' '}
                      {idToRow.get(row.parent_id)?.name ?? `#${row.parent_id}`}
                    </span>
                  ) : null}
                  <span>排序 {row.sort_order ?? '—'}</span>
                  <span>#{row.id}</span>
                </div>
                <AdminRecordActions
                  actions={[
                    {
                      key: 'delete',
                      label: '删除',
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => handleDelete(row),
                    },
                    {
                      key: 'edit',
                      label: '编辑',
                      icon: <EditOutlined />,
                      primary: true,
                      onClick: () => setEditing(row),
                    },
                    {
                      key: 'animes',
                      label: '番剧',
                      icon: <UnorderedListOutlined />,
                      primary: true,
                      onClick: () => setManaging(row),
                    },
                  ]}
                />
              </article>
            )}
          />
        ) : null}
      </Card>

      <EditModal
        editing={editing}
        all={listQuery.data ?? []}
        onClose={() => setEditing(null)}
        onSubmit={(values) => {
          if (editing === 'new') {
            createMutation.mutate({
              name: values.name,
              slug: values.slug,
              parent_id: values.parent_id,
              is_active: values.is_active,
              sort_order: values.sort_order,
            })
          } else if (editing) {
            updateMutation.mutate({ id: editing.id, patch: values })
          }
        }}
        saving={createMutation.isPending || updateMutation.isPending}
      />

      <AnimeMembershipDrawer
        open={!!managing}
        targetId={managing?.id ?? null}
        targetTitle={managing?.name ?? ''}
        field="type_id"
        label="类型"
        onClose={() => setManaging(null)}
      />
    </>
  )
}

/* ── Edit modal ───────────────────────────────────────────────── */

function buildTreeData(all: Row[], excludeId?: number) {
  const byParent = new Map<number | null, Row[]>()
  for (const r of all) {
    if (r.id === excludeId) continue
    const key = r.parent_id ?? null
    const arr = byParent.get(key) ?? []
    arr.push(r)
    byParent.set(key, arr)
  }
  function build(parent: number | null): Array<{
    value: number
    title: string
    children?: Array<{ value: number; title: string }>
  }> {
    const children = byParent.get(parent) ?? []
    return children.map((c) => ({
      value: c.id,
      title: c.name,
      children: build(c.id),
    }))
  }
  return build(null)
}

function EditModal({
  editing,
  all,
  onClose,
  onSubmit,
  saving,
}: {
  editing: Row | 'new' | null
  all: Row[]
  onClose: () => void
  onSubmit: (v: FormValues) => void
  saving: boolean
}) {
  const [form] = Form.useForm<FormValues>()
  const [fullscreen, setFullscreen] = useState(false)
  const isNew = editing === 'new'
  const row = editing && editing !== 'new' ? editing : null

  useEffect(() => {
    if (!editing) {
      form.resetFields()
      setFullscreen(false)
      return
    }
    if (isNew) {
      form.setFieldsValue({
        name: '',
        slug: '',
        parent_id: null,
        is_active: true,
        sort_order: null,
      })
    } else if (row) {
      form.setFieldsValue({
        name: row.name,
        slug: row.slug,
        parent_id: row.parent_id,
        is_active: row.is_active ?? true,
        sort_order: row.sort_order,
      })
    }
  }, [editing, isNew, row, form])

  const treeData = useMemo(() => buildTreeData(all, row?.id), [all, row?.id])

  return (
    <Modal
      open={!!editing}
      title={
        <Space
          align="center"
          style={{ width: '100%', justifyContent: 'space-between' }}
        >
          <span>{isNew ? '新增类型' : `编辑 · ${row?.name ?? ''}`}</span>
          <Button
            type="text"
            size="small"
            icon={
              fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
            }
            onClick={() => setFullscreen((v) => !v)}
            aria-label={fullscreen ? '退出全屏' : '全屏编辑'}
            style={{ marginRight: 24 }}
          />
        </Space>
      }
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={saving}
      okText="保存"
      cancelText="取消"
      destroyOnHidden
      width={fullscreen ? '100%' : 520}
      className={fullscreen ? 'app-fullscreen-modal' : undefined}
    >
      <Form<FormValues>
        form={form}
        // Modal 内容晚于 effect 挂载，setFieldsValue 首次打开会落空；用 initialValues 兜底回显
        initialValues={
          row
            ? {
                name: row.name,
                slug: row.slug,
                parent_id: row.parent_id,
                is_active: row.is_active ?? true,
                sort_order: row.sort_order,
              }
            : { is_active: true, parent_id: null, sort_order: null }
        }
        layout="vertical"
        requiredMark={false}
        onFinish={onSubmit}
        preserve={false}
      >
        <Form.Item
          label="名称"
          name="name"
          rules={[
            { required: true, message: '请输入名称' },
            { max: 100, message: '最长 100 字' },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Slug"
          name="slug"
          rules={[
            { required: true, message: '请输入 slug' },
            {
              pattern: /^[a-z0-9-_]+$/,
              message: '仅小写字母、数字、连字符、下划线',
            },
          ]}
          extra="URL / 程序内部用。仅小写字母、数字、- 和 _。"
        >
          <Input />
        </Form.Item>
        <Form.Item label="父类型" name="parent_id">
          <TreeSelect
            treeData={treeData}
            allowClear
            placeholder="无（顶级类型）"
            treeDefaultExpandAll
            showSearch
            treeNodeFilterProp="title"
          />
        </Form.Item>
        <Space size="large" wrap>
          <Form.Item
            label="启用"
            name="is_active"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="排序"
            name="sort_order"
            style={{ marginBottom: 0 }}
            extra="数字越小越靠前；留空使用默认。"
          >
            <InputNumber min={0} max={9999} style={{ width: 120 }} />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  )
}
